import httpService from "./httpService";

/**
 * Contract exclusion as returned by the profile exclusions API
 */
export interface ContractExclusion {
  id: number;
  userId: string;
  conId: number;
  message: string | null;
  createdAtUtc: string;
  createdBy: string;
}

/**
 * Response from GET /v2/profile/contract/exclusions
 */
export interface GetExclusionsResponse {
  exclusions: ContractExclusion[];
  count: number;
}

/**
 * Request body for POST /v2/profile/contract/exclusions
 */
export interface CreateExclusionRequest {
  conId: number;
  message?: string;
}

/**
 * Response from POST /v2/profile/contract/exclusions
 */
export interface CreateExclusionResponse {
  message: string;
  exclusion: ContractExclusion;
}

/**
 * Response from DELETE /v2/profile/contract/exclusions/:conId
 */
export interface DeleteExclusionResponse {
  message: string;
}

/**
 * Service for managing contract exclusions for the authenticated user.
 * Uses the /v2/profile/contract/exclusions endpoints.
 */
class ProfileExclusionsService {
  private readonly basePath = "/v2/profile/contract/exclusions";

  /**
   * Get all contract exclusions for the authenticated user
   */
  async getExclusions(): Promise<GetExclusionsResponse> {
    const response = await httpService.get<GetExclusionsResponse>(this.basePath);
    return response.data;
  }

  /**
   * Create a new contract exclusion for the authenticated user
   */
  async createExclusion(conId: number, message?: string): Promise<ContractExclusion> {
    const payload: CreateExclusionRequest = { conId };
    if (message) {
      payload.message = message;
    }
    const response = await httpService.post<CreateExclusionResponse>(this.basePath, payload);
    return response.data.exclusion;
  }

  /**
   * Delete a contract exclusion by conId for the authenticated user
   */
  async deleteExclusion(conId: number): Promise<void> {
    await httpService.delete<DeleteExclusionResponse>(`${this.basePath}/${conId}`);
  }
}

export const profileExclusionsService = new ProfileExclusionsService();
export default profileExclusionsService;

